import { useCallback } from 'react';

import { ApolloCache, gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  ConvertInboxEntryKindInput,
  CreateChildInboxEntriesInput,
  InboxEntry,
  SetInboxEntryChildOrderKeysInput,
  SetInboxEntryParentInput,
  SetSpecReviewersInput,
  SetSpecStatusInput,
} from '@this+that/models';

import { INBOX_ENTRY_CORE_FIELDS } from './inboxEntryFragments';
import { FETCH_POLICY_CACHE_AND_NETWORK } from '../utils/constants';
import { appendChildrenToTreeCache } from '../utils/taskTree/taskTreeCache';

export const GET_INBOX_ENTRY_TREE = gql`
  query getInboxEntryTree($inboxEntryId: ID!) {
    getInboxEntryTree(inboxEntryId: $inboxEntryId) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface GetInboxEntryTreeData {
  getInboxEntryTree: InboxEntry[];
}

export function useGetInboxEntryTree(inboxEntryId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetInboxEntryTreeData>(
    GET_INBOX_ENTRY_TREE,
    {
      variables: { inboxEntryId },
      skip: !inboxEntryId,
      fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
    },
  );

  return { entries: data?.getInboxEntryTree, loading, error, refetch };
}

const SET_INBOX_ENTRY_PARENT = gql`
  mutation setInboxEntryParent($input: SetInboxEntryParentInput!) {
    setInboxEntryParent(input: $input) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface SetInboxEntryParentData {
  setInboxEntryParent: InboxEntry;
}

export function useSetInboxEntryParent() {
  const [mutate, { loading }] = useMutation<SetInboxEntryParentData>(
    SET_INBOX_ENTRY_PARENT,
  );

  const setInboxEntryParent = useCallback(
    async (input: SetInboxEntryParentInput) => {
      const { data, error } = await mutate({ variables: { input } });
      if (error) {
        throw error;
      }
      return data?.setInboxEntryParent;
    },
    [mutate],
  );

  return { setInboxEntryParent, loading };
}

const SET_INBOX_ENTRY_CHILD_ORDER_KEYS = gql`
  mutation setInboxEntryChildOrderKeys($input: SetInboxEntryChildOrderKeysInput!) {
    setInboxEntryChildOrderKeys(input: $input) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface SetInboxEntryChildOrderKeysData {
  setInboxEntryChildOrderKeys: InboxEntry[];
}

export function useSetInboxEntryChildOrderKeys() {
  const [mutate, { loading }] = useMutation<SetInboxEntryChildOrderKeysData>(
    SET_INBOX_ENTRY_CHILD_ORDER_KEYS,
  );

  const setInboxEntryChildOrderKeys = useCallback(
    async (input: SetInboxEntryChildOrderKeysInput) => {
      const { data, error } = await mutate({ variables: { input } });
      if (error) {
        throw error;
      }
      return data?.setInboxEntryChildOrderKeys ?? [];
    },
    [mutate],
  );

  return { setInboxEntryChildOrderKeys, loading };
}

const CREATE_CHILD_INBOX_ENTRIES = gql`
  mutation createChildInboxEntries($input: CreateChildInboxEntriesInput!) {
    createChildInboxEntries(input: $input) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface CreateChildInboxEntriesData {
  createChildInboxEntries: InboxEntry[];
}

// The new rows reach the open tree through the cache, never the board store.
function appendCreatedChildren(
  cache: ApolloCache,
  parentInboxEntryId: string,
  created: InboxEntry[] | undefined,
) {
  if (created?.length) {
    appendChildrenToTreeCache(cache, parentInboxEntryId, created);
  }
}

export function useCreateChildInboxEntries() {
  const [mutate, { loading }] = useMutation<CreateChildInboxEntriesData>(
    CREATE_CHILD_INBOX_ENTRIES,
  );

  const createChildInboxEntries = useCallback(
    async (input: CreateChildInboxEntriesInput) => {
      const { data, error } = await mutate({
        variables: { input },
        update: (cache, result) =>
          appendCreatedChildren(
            cache,
            input.parentInboxEntryId,
            result.data?.createChildInboxEntries,
          ),
      });
      if (error) {
        throw error;
      }
      return data?.createChildInboxEntries ?? [];
    },
    [mutate],
  );

  return { createChildInboxEntries, loading };
}

const CONVERT_INBOX_ENTRY_KIND = gql`
  mutation convertInboxEntryKind($input: ConvertInboxEntryKindInput!) {
    convertInboxEntryKind(input: $input) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface ConvertInboxEntryKindData {
  convertInboxEntryKind: InboxEntry;
}

export function useConvertInboxEntryKind() {
  const [mutate, { loading }] = useMutation<ConvertInboxEntryKindData>(
    CONVERT_INBOX_ENTRY_KIND,
  );

  const convertInboxEntryKind = useCallback(
    async (input: ConvertInboxEntryKindInput) => {
      const { data, error } = await mutate({ variables: { input } });
      if (error) {
        throw error;
      }
      return data?.convertInboxEntryKind;
    },
    [mutate],
  );

  return { convertInboxEntryKind, loading };
}

const SET_SPEC_STATUS = gql`
  mutation setSpecStatus($input: SetSpecStatusInput!) {
    setSpecStatus(input: $input) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface SetSpecStatusData {
  setSpecStatus: InboxEntry;
}

export function useSetSpecStatus() {
  const [mutate, { loading }] = useMutation<SetSpecStatusData>(SET_SPEC_STATUS);

  const setSpecStatus = useCallback(
    async (input: SetSpecStatusInput) => {
      const { data, error } = await mutate({ variables: { input } });
      if (error) {
        throw error;
      }
      return data?.setSpecStatus;
    },
    [mutate],
  );

  return { setSpecStatus, loading };
}

const SET_SPEC_REVIEWERS = gql`
  mutation setSpecReviewers($input: SetSpecReviewersInput!) {
    setSpecReviewers(input: $input) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface SetSpecReviewersData {
  setSpecReviewers: InboxEntry;
}

export function useSetSpecReviewers() {
  const [mutate, { loading }] =
    useMutation<SetSpecReviewersData>(SET_SPEC_REVIEWERS);

  const setSpecReviewers = useCallback(
    async (input: SetSpecReviewersInput) => {
      const { data, error } = await mutate({ variables: { input } });
      if (error) {
        throw error;
      }
      return data?.setSpecReviewers;
    },
    [mutate],
  );

  return { setSpecReviewers, loading };
}

const APPROVE_SPEC = gql`
  mutation approveSpec($inboxEntryId: String!) {
    approveSpec(inboxEntryId: $inboxEntryId) {
      ...InboxEntryCoreFields
    }
  }
  ${INBOX_ENTRY_CORE_FIELDS}
`;

interface ApproveSpecData {
  approveSpec: InboxEntry;
}

export function useApproveSpec() {
  const [mutate, { loading }] = useMutation<ApproveSpecData>(APPROVE_SPEC);

  const approveSpec = useCallback(
    async (inboxEntryId: string) => {
      const { data, error } = await mutate({ variables: { inboxEntryId } });
      if (error) {
        throw error;
      }
      return data?.approveSpec;
    },
    [mutate],
  );

  return { approveSpec, loading };
}
